"use client";

import { useEffect, useRef, useState } from "react";
import { Icon, type IconName } from "@/components/ui/Icon";
import { contactTopics, contactBudgets, topicLabel } from "@/lib/contact-schema";
import { trackEvent, TRACKING_EVENTS } from "@/lib/tracking";
import { klickHerkunft, marketingErlaubt } from "@/lib/klick-id";
import { userDataGehasht } from "@/lib/erweiterte-conversion";

type Status = "idle" | "sending" | "success" | "error";

const STEPS = ["Thema", "Budget", "Kontakt"] as const;

export function ContactForm({
  initialTopic = "",
  initialMessage = "",
}: {
  initialTopic?: string;
  initialMessage?: string;
}) {
  // Mit vorbelegtem Thema (z. B. aus einer Landingpage) gleich beim Budget starten.
  const [step, setStep] = useState(initialTopic ? 1 : 0);
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const [topic, setTopic] = useState(initialTopic);
  const [budget, setBudget] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: initialMessage,
    consent: false,
    website: "", // Honeypot
  });

  /*
   * Zeitfalle: signierter Zeitstempel vom Server, beim Laden geholt (die
   * Seite selbst ist statisch, ein eingebetteter Wert wäre so alt wie der
   * Build). Ohne gültigen Token verwirft der Server die Anfrage still.
   */
  const tokenRef = useRef<{ token: string; at: number } | null>(null);
  async function loadToken() {
    try {
      const res = await fetch("/api/form-token?form=contact", { cache: "no-store" });
      const data = await res.json();
      if (res.ok && data.ok && typeof data.token === "string") {
        tokenRef.current = { token: data.token, at: Date.now() };
      }
    } catch {
      // Beim Absenden wird erneut versucht.
    }
  }
  useEffect(() => {
    void loadToken();
  }, []);

  /** Gültiger Token, notfalls frisch geholt; wartet die Mindestzeit ab. */
  async function ensureToken(): Promise<string | null> {
    const t = tokenRef.current;
    // Älter als 20 h (Server akzeptiert 24 h) oder nie geladen → neu holen.
    if (!t || Date.now() - t.at > 20 * 60 * 60 * 1000) {
      tokenRef.current = null;
      await loadToken();
    }
    const cur = tokenRef.current;
    if (!cur) return null;
    const wait = 3500 - (Date.now() - cur.at);
    if (wait > 0) await new Promise((r) => setTimeout(r, wait));
    return cur.token;
  }

  const update = (k: keyof typeof form, v: string | boolean) =>
    setForm((f) => ({ ...f, [k]: v }));

  function pickTopic(value: string) {
    setTopic(value);
    setStep(1);
  }
  function pickBudget(value: string) {
    setBudget(value);
    setStep(2);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");
    try {
      const formToken = await ensureToken();
      if (!formToken) {
        setStatus("error");
        setErrorMsg("Das Formular konnte nicht geladen werden. Bitte laden Sie die Seite neu oder schreiben Sie uns direkt.");
        return;
      }
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Herkunft mitschicken: Klick-ID und Kampagne, soweit bekannt.
        // Sie stehen bewusst NICHT als versteckte Eingabefelder im
        // Formular – dort könnte sie ein Besucher ändern, und Bots füllen
        // versteckte Felder gern aus. Zur Absendezeit ausgelesen ist
        // sicherer und ergibt dasselbe Ergebnis.
        body: JSON.stringify({
          topic,
          budget,
          ...form,
          formToken,
          ...klickHerkunft(),
          seite: typeof window !== "undefined" ? window.location.pathname : "",
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setStatus("error");
        setErrorMsg(data.error ?? "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.");
        return;
      }
      // Token ist verbraucht – für eine weitere Anfrage einen neuen holen.
      tokenRef.current = null;
      void loadToken();
      setStatus("success");
      // Conversion für GA4/Google Ads.
      //
      // `user_data` nur bei erteilter Marketing-Einwilligung und
      // ausschließlich als SHA-256-Hash – siehe lib/erweiterte-conversion.ts.
      // Klartext im dataLayer könnte jedes Tag im Container mitlesen.
      const herkunft = klickHerkunft();
      const basis: Record<string, string | number | boolean> = {
        topic: topic || "unbekannt",
        ...(herkunft.gclid ? { gclid: herkunft.gclid } : {}),
        ...(herkunft.utm_campaign ? { utm_campaign: herkunft.utm_campaign } : {}),
      };
      if (marketingErlaubt()) {
        const ud = await userDataGehasht(form.email, form.phone);
        if (Object.keys(ud).length > 0) {
          trackEvent(TRACKING_EVENTS.lead, { ...basis, user_data: ud } as never);
        } else {
          trackEvent(TRACKING_EVENTS.lead, basis);
        }
      } else {
        trackEvent(TRACKING_EVENTS.lead, basis);
      }
    } catch {
      setStatus("error");
      setErrorMsg("Netzwerkfehler. Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut.");
    }
  }

  if (status === "success") {
    return (
      <div className="card-highlight flex flex-col items-center p-10 text-center">
        <span className="grid h-16 w-16 place-items-center rounded-full bg-cyan-400/15 text-accent ring-1 ring-cyan-400/30">
          <Icon name="check" size={34} strokeWidth={2.5} />
        </span>
        <h3 className="mt-6 font-display text-2xl font-bold text-fg">Vielen Dank!</h3>
        <p className="mt-2 max-w-sm text-fg-muted">
          Ihre Anfrage ist bei uns eingegangen. Wir melden uns in der Regel innerhalb eines Werktags.
          Eine Bestätigung liegt in Ihrem Postfach.
        </p>
      </div>
    );
  }

  const canSubmit = form.name.length >= 2 && /.+@.+\..+/.test(form.email) && form.message.length >= 10 && form.consent;

  return (
    <div className="relative">
      {/*
        Farbschimmer hinter dem Formular. Ohne ihn sitzt die Karte optisch auf
        derselben Ebene wie die Kontaktkacheln daneben – das Formular ist aber
        das Ziel der Seite und muss als solches erkennbar sein.
      */}
      <div className="card-highlight relative overflow-hidden">
        {/* Kopfband – benennt das Ziel und greift die Beschriftung der CTA-Buttons auf. */}
        <div className="border-b border-line bg-brand-500/8 px-6 py-6 md:px-8">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-brand-ink">
            <span className="h-1.5 w-1.5 rounded-full bg-accent" />
            Kostenlos &amp; unverbindlich
          </span>
          <h2 className="mt-2.5 font-display text-2xl font-bold leading-tight text-fg">
            Erstgespräch sichern
          </h2>
          <p className="mt-1.5 text-sm text-fg-muted">
            Drei kurze Schritte – dauert weniger als eine Minute.
          </p>
        </div>

        <div className="p-6 md:p-8">
      {/* Fortschritt */}
      <div className="mb-8 flex items-center gap-2">
        {STEPS.map((label, i) => (
          <div key={label} className="flex flex-1 items-center gap-2">
            <button
              type="button"
              onClick={() => i < step && setStep(i)}
              className={`flex items-center gap-2 text-sm font-medium ${
                i <= step ? "text-fg" : "text-fg-subtle"
              } ${i < step ? "cursor-pointer" : "cursor-default"}`}
            >
              <span
                className={`grid h-7 w-7 place-items-center rounded-full text-xs font-bold transition-colors ${
                  i < step
                    ? "bg-cyan-400/20 text-accent"
                    : i === step
                      ? "bg-brand-500 text-white"
                      : "bg-surface-2 text-fg-subtle"
                }`}
              >
                {i < step ? <Icon name="check" size={14} strokeWidth={3} /> : i + 1}
              </span>
              <span className="hidden sm:inline">{label}</span>
            </button>
            {i < STEPS.length - 1 && (
              <span className={`h-px flex-1 ${i < step ? "bg-cyan-400/40" : "bg-surface-3"}`} />
            )}
          </div>
        ))}
      </div>

      {/* Schritt 1: Thema */}
      {step === 0 && (
        <div>
          <h3 className="font-display text-xl font-semibold text-fg">Worum geht es?</h3>
          <p className="mt-1 text-sm text-fg-muted">Wählen Sie ein Thema – ein Klick genügt.</p>
          <div className="mt-5 grid gap-2.5 sm:grid-cols-2">
            {contactTopics.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => pickTopic(t.value)}
                className={`group flex items-center gap-3 rounded-xl border p-4 text-left transition-all ${
                  topic === t.value
                    ? "border-brand-400/50 bg-brand-500/10"
                    : "border-line bg-surface-2 hover:border-brand-400/40 hover:bg-surface-2"
                }`}
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-surface-2 text-brand-ink">
                  <Icon name={t.icon as IconName} size={18} />
                </span>
                <span className="text-sm font-medium text-fg">{t.label}</span>
                <Icon name="arrow-right" size={16} className="ml-auto text-fg-subtle transition-transform group-hover:translate-x-1" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Schritt 2: Budget */}
      {step === 1 && (
        <div>
          <button type="button" onClick={() => setStep(0)} className="mb-4 inline-flex items-center gap-1 text-sm text-fg-subtle hover:text-fg">
            <Icon name="arrow-right" size={15} className="rotate-180" /> zurück
          </button>
          <h3 className="font-display text-xl font-semibold text-fg">Haben Sie ein Budget im Kopf?</h3>
          <p className="mt-1 text-sm text-fg-muted">
            Nur zur groben Orientierung – kein Problem, wenn Sie es noch nicht wissen.
          </p>
          <div className="mt-5 grid gap-2.5">
            {contactBudgets.map((b) => (
              <button
                key={b.value}
                type="button"
                onClick={() => pickBudget(b.value)}
                className={`flex items-center justify-between rounded-xl border p-4 text-left transition-all ${
                  budget === b.value
                    ? "border-brand-400/50 bg-brand-500/10"
                    : "border-line bg-surface-2 hover:border-brand-400/40 hover:bg-surface-2"
                }`}
              >
                <span className="text-sm font-medium text-fg">{b.label}</span>
                <Icon name="arrow-right" size={16} className="text-fg-subtle" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Schritt 3: Kontaktdaten */}
      {step === 2 && (
        <form onSubmit={submit} className="flex flex-col gap-4">
          <button type="button" onClick={() => setStep(1)} className="inline-flex w-fit items-center gap-1 text-sm text-fg-subtle hover:text-fg">
            <Icon name="arrow-right" size={15} className="rotate-180" /> zurück
          </button>
          <div className="rounded-xl bg-surface-2 px-4 py-3 text-sm text-fg-muted">
            <span className="text-fg-subtle">Ihr Anliegen:</span>{" "}
            <strong className="text-fg">{topic ? topicLabel(topic) : "—"}</strong>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name *" value={form.name} onChange={(v) => update("name", v)} placeholder="Max Mustermann" autoComplete="name" required />
            <Field label="E-Mail *" type="email" value={form.email} onChange={(v) => update("email", v)} placeholder="max@firma.at" autoComplete="email" required />
            <Field label="Telefon" type="tel" value={form.phone} onChange={(v) => update("phone", v)} placeholder="+43 …" autoComplete="tel" />
            <Field label="Firma" value={form.company} onChange={(v) => update("company", v)} placeholder="Musterfirma GmbH" autoComplete="organization" />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-fg-muted">Ihre Nachricht *</label>
            <textarea
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              rows={4}
              required
              placeholder="Beschreiben Sie kurz Ihr Vorhaben oder Ihre Frage …"
              className="w-full resize-y rounded-xl border border-line bg-surface-2 px-4 py-3 text-sm text-fg placeholder:text-fg-subtle focus:border-brand-400/60 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
          </div>

          {/* Honeypot – für Menschen unsichtbar */}
          <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden" tabIndex={-1}>
            <label>
              Website (bitte leer lassen)
              <input name="website" value={form.website} onChange={(e) => update("website", e.target.value)} tabIndex={-1} autoComplete="off" />
            </label>
          </div>

          <label className="flex items-start gap-3 text-sm text-fg-muted">
            <input
              type="checkbox"
              checked={form.consent}
              onChange={(e) => update("consent", e.target.checked)}
              required
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-line-strong bg-surface-2 accent-[color:var(--color-brand-500)]"
            />
            <span>
              Ich stimme zu, dass meine Angaben zur Bearbeitung meiner Anfrage verarbeitet werden.
              Details in der <a href="/datenschutz" className="text-brand-ink underline">Datenschutzerklärung</a>. *
            </span>
          </label>

          {status === "error" && (
            <p className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-danger">
              {errorMsg}
            </p>
          )}

          <button
            type="submit"
            disabled={status === "sending" || !canSubmit}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-brand-500 px-7 py-3.5 font-semibold text-white shadow-cta transition-all hover:bg-brand-400 disabled:pointer-events-none disabled:opacity-50"
          >
            {status === "sending" ? (
              <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-line-strong border-t-white" />
                Wird gesendet …
              </>
            ) : (
              <>
                Anfrage absenden <Icon name="arrow-right" size={19} />
              </>
            )}
          </button>
          <p className="text-center text-xs text-fg-subtle">
            Kostenlos & unverbindlich · Antwort in der Regel innerhalb eines Werktags
          </p>
        </form>
      )}
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  autoComplete,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-fg-muted">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={required}
        className="w-full rounded-xl border border-line bg-surface-2 px-4 py-3 text-sm text-fg placeholder:text-fg-subtle focus:border-brand-400/60 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
      />
    </div>
  );
}
